import React from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { GraduationCap, Clock, FileText, Target } from 'lucide-react';

/**
 * CeAssessmentIntroCard - Hero-style invitation card for CE assessment
 * 
 * Pixel-perfect port from Polymet prototype with succession theme variables.
 * Per memory: avoid bg-white, use theme vars for dark mode compatibility.
 */
const CeAssessmentIntroCard = ({
  creditHours,
  questionCount,
  estimatedMinutes,
  passingThreshold,
  courseName,
  sponsorName,
  onStart,
  onSkip,
  className = '',
}) => {
  const intl = useIntl();

  return (
    <div
      className={`
        border-2 border-succession-primary/20
        bg-gradient-to-br from-succession-primary/5 to-succession-bg-primary
        rounded-xl
        ${className}
      `}
    >
      <div className="p-8 space-y-6">
        {/* Header with Badge */}
        <div className="space-y-4">
          {/* Badge: inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-medium */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-succession-primary/10 text-succession-primary rounded-full text-sm font-medium">
            <GraduationCap className="w-4 h-4" />
            {intl.formatMessage({ id: 'succession.plans.ce_intro_badge' })}
          </div>

          <div className="space-y-2">
            <h3 className="text-2xl font-bold text-succession-text-primary">
              {intl.formatMessage({ id: 'succession.plans.ce_intro_earn_title' }, { hours: creditHours })}
            </h3>
            <p className="text-succession-text-secondary">
              {intl.formatMessage({ id: 'succession.plans.ce_intro_description' }, { courseName })}
            </p>
            {sponsorName && (
              <p className="text-sm text-succession-text-secondary">
                {intl.formatMessage({ id: 'succession.plans.ce_intro_sponsored_by' }, { sponsorName })}
              </p>
            )}
          </div>
        </div>

        {/* Stats Grid: grid-cols-3 gap-4 */}
        <div className="grid grid-cols-3 gap-4">
          {/* Questions stat */}
          <div className="flex items-start gap-3 p-4 bg-succession-bg-primary border border-succession-border rounded-lg">
            <FileText className="w-5 h-5 text-succession-text-secondary mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-2xl font-bold text-succession-text-primary">{questionCount}</p>
              <p className="text-xs text-succession-text-secondary">{intl.formatMessage({ id: 'succession.plans.ce_intro_questions' })}</p>
            </div>
          </div>

          {/* Time stat */}
          <div className="flex items-start gap-3 p-4 bg-succession-bg-primary border border-succession-border rounded-lg">
            <Clock className="w-5 h-5 text-succession-text-secondary mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-2xl font-bold text-succession-text-primary">{estimatedMinutes}</p>
              <p className="text-xs text-succession-text-secondary">{intl.formatMessage({ id: 'succession.plans.ce_intro_minutes' })}</p>
            </div>
          </div>

          {/* Passing score stat */}
          <div className="flex items-start gap-3 p-4 bg-succession-bg-primary border border-succession-border rounded-lg">
            <Target className="w-5 h-5 text-succession-text-secondary mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-2xl font-bold text-succession-text-primary">{passingThreshold}%</p>
              <p className="text-xs text-succession-text-secondary">{intl.formatMessage({ id: 'succession.plans.ce_intro_to_pass' })}</p>
            </div>
          </div>
        </div>

        {/* Actions: flex items-center gap-3 pt-2 */}
        <div className="flex items-center gap-3 pt-2">
          <button
            onClick={onStart}
            className="flex-1 px-6 py-3 text-lg font-medium text-white bg-succession-primary hover:bg-succession-primary/90 rounded-lg transition-all duration-300"
          >
            {intl.formatMessage({ id: 'succession.plans.ce_intro_start' })}
          </button>
          <button
            onClick={onSkip}
            className="px-6 py-3 text-lg font-medium text-succession-text-secondary hover:text-succession-text-primary hover:bg-succession-hover-bg rounded-lg transition-all duration-300"
          >
            {intl.formatMessage({ id: 'succession.plans.ce_intro_skip' })}
          </button>
        </div>

        {/* Fine Print: text-xs text-muted-foreground text-center */}
        <p className="text-xs text-succession-text-secondary text-center">
          {intl.formatMessage({ id: 'succession.plans.ce_intro_fine_print' })}
        </p>
      </div>
    </div>
  );
};

CeAssessmentIntroCard.propTypes = {
  creditHours: PropTypes.number.isRequired,
  questionCount: PropTypes.number.isRequired,
  estimatedMinutes: PropTypes.number.isRequired,
  passingThreshold: PropTypes.number.isRequired,
  courseName: PropTypes.string.isRequired,
  sponsorName: PropTypes.string,
  onStart: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired,
  className: PropTypes.string,
};

CeAssessmentIntroCard.defaultProps = {
  sponsorName: null,
  className: '',
};

export default CeAssessmentIntroCard;

